import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  name: z.string().trim().min(2, "اسم الفصل مطلوب").max(100),
  age_group: z.string().optional(),
  capacity: z.coerce.number().int().min(1, "السعة يجب أن تكون 1 على الأقل").max(100),
  teacher_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  staff: { id: string; full_name: string }[];
  onSuccess: () => void;
}

export default function AddClassDialog({ staff, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const { toast } = useToast();
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { capacity: 25 },
  });

  const onSubmit = async (data: FormData) => {
    if (!profile?.nursery_id) return;
    const { error } = await supabase.from("classes").insert({
      name: data.name,
      age_group: data.age_group || null,
      capacity: data.capacity,
      teacher_id: data.teacher_id || null,
      nursery_id: profile.nursery_id,
    });
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "تم إضافة الفصل بنجاح" });
    reset();
    setOpen(false);
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Plus className="w-4 h-4 ml-2" />
          إضافة فصل
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة فصل جديد</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>اسم الفصل *</Label>
            <Input {...register("name")} placeholder="فصل الورود" />
            {errors.name && <p className="text-destructive text-xs mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label>الفئة العمرية</Label>
            <Input {...register("age_group")} placeholder="3-4 سنوات" />
          </div>
          <div>
            <Label>السعة *</Label>
            <Input type="number" {...register("capacity")} />
            {errors.capacity && <p className="text-destructive text-xs mt-1">{errors.capacity.message}</p>}
          </div>
          <div>
            <Label>المعلمة</Label>
            <Select onValueChange={(v) => setValue("teacher_id", v)}>
              <SelectTrigger><SelectValue placeholder="اختر المعلمة" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
